import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setPaymentInfo, cleanPaymentInfo } from "redux/actions";
import { usePaymentForm } from "customHook/usePaymentForm";
import ProgressBar from "components/ProgressBar";
import Button from "components/Button";
import Input from "components/Input";
import Checkbox from "components/Checkbox";
import { usePlacesWidget } from "react-google-autocomplete";
import Inputmask from "react-input-mask";
import { creditCardMask, expiryDateMask, cvvMask } from "./inputMasks";
import ModalGeneralFailed from "screens/ModalGeneralFailed";
import ModalGeneralSuccessfully from "screens/ModalGeneralSuccessfully";
import ModalPartialAuthorizationAccepted from "screens/ModalPartialAuthorizationAccepted";
import { useMutation } from "@apollo/client";
import { CREATE_PAYMENT_CARD } from "graphql/service";
import { detectCardType } from "utils/detectCardType";
import checkMark from "assets/svg/checkMark.svg";
import {
  Head,
  Container,
  Content,
  Footer,
  Row
} from "./styles";

const CreditCard = ({status}) => {

  const groupId = useParams().groupId;
  const navigate = useNavigate();

  const [addCreditCardSuccessfullyIsOpen, setAddCreditCardSuccessfullyIsOpen] = useState(false);
  const [addCreditCardFailedIsOpen, setAddCreditCardFailedIsOpen] = useState(false);
  const [partialAuthorizationIsOpen, setPartialAuthorizationIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [credit, setCredit] = useState(0);
  const [balance, setBalance] = useState(0);

  const [creditCardType, setCreditCardType] = useState("");
  const [coverInput, setCoverInput] = useState("");
  const dispatch = useDispatch();
  const [isOTAPrepayment, setIsOTAPrepayment] = useState(true);
  const paymentInfo = useSelector(
    (state) => state.paymentInfoReducer.paymentInfo
  );
  const {
    paymentError,
    setPaymentError,
    isValidatePaymentInfo,
  } = usePaymentForm(creditCardType);

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_GOOGLE_APIKEY,
    onPlaceSelected: (place) => {
      const components = place.address_components || [];

      const getComponent = (type) =>
        components.find((component) => component.types.includes(type))
          ?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      const streetAddressLine2 = ""; // Google Autocomplete doesn't return this
      const city =
        getComponent("locality") || getComponent("sublocality") || "";
      const province = getComponent("administrative_area_level_1");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");
      dispatch(
        setPaymentInfo({
          streetAddress,
          streetAddressLine2,
          city,
          province,
          postalCode,
          country,
        })
      );
    },
    options: {
      types: ["address"],
    },
  });

  const [createPaymentCard, { loading }] = useMutation(CREATE_PAYMENT_CARD, {
    onCompleted: (data) => {
      if (data) {
        setAddCreditCardFailedIsOpen(false);
        setAddCreditCardSuccessfullyIsOpen(true);
      }
    },
    onError: (error) => {
      setAddCreditCardSuccessfullyIsOpen(false);
      setAddCreditCardFailedIsOpen(true);
    },
  });

  useEffect(() => {
    return () => {
      dispatch(cleanPaymentInfo());
      setCoverInput("");
      creditCardMask.mask = "9999 9999 9999 9999";
    };
  }, []);

  const onChangeCreditCard = (e) => {
    setCoverInput(e.target.value);
    dispatch(setPaymentInfo({ creditCardNumber: e.target.value }));
    if (e.target.value.length < 19) {
      setCreditCardType("");
      setPaymentError((paymentError) => ({
        ...paymentError,
        creditCardNumberError: false,
      }));
    } else {
      const type = detectCardType(e.target.value.replace(/\s/g, ""));
      if (type.length > 0) {
        setPaymentError((paymentError) => ({
          ...paymentError,
          creditCardNumberError: false,
        }));
      } else {
        setPaymentError((paymentError) => ({
          ...paymentError,
          creditCardNumberError: true,
        }));
      }
      setCreditCardType(type);
    }
  };

  const handleContinue = () => {
    if (loading) return;
    if (isValidatePaymentInfo()) {
      createPaymentCard({
        variables: {
          input: {
            cardHolderName: paymentInfo.cardHolderName,
            creditCardNumber: paymentInfo.creditCardNumber.replace(/\s/g, ""),
            cardExpirationDate: paymentInfo.cardExpirationDate.replace(
              /[\s/]/g,
              ""
            ),
            cardCVV: paymentInfo.cardCVV,
            streetAddress: paymentInfo.streetAddress,
            streetAddressLine2: paymentInfo.streetAddressLine2,
            city: paymentInfo.city,
            province: paymentInfo.province,
            postalCode: paymentInfo.postalCode,
            country: paymentInfo.country,
            groupId: groupId,
            isPrimary: true,
            isOTAPrepayment: isOTAPrepayment,
          },
        },
      });
    }
  };

  return (
    <Container>
      <ProgressBar step={3} groupId={groupId} status={status}/>
      <Content>
        <Head>Add Credit Card</Head>
        <Row>
          <Input
            required
            title="CARDHOLDER NAME"
            value={paymentInfo.cardHolderName}
            error={paymentError.cardHolderNameError}
            onChange={(e) => {
              dispatch(setPaymentInfo({ cardHolderName: e.target.value }));
              if (paymentError.cardHolderNameError) {
                setPaymentError((paymentError) => ({
                  ...paymentError,
                  cardHolderNameError: false,
                }));
              }
            }}
            additionalstyle={`
              flex-basis: 100%;
            `}
          />
        </Row>
        <Row>
          <Inputmask
            mask={creditCardMask.mask}
            maskPlaceholder=""
            value={coverInput}
            error={paymentError.creditCardNumberError}
            errorMessage={"The card number entered is not valid."}
            onChange={onChangeCreditCard}
          >
            <Input
              required
              title="CARD NUMBER"
              maxLength={19}
              additionalstyle={`
                width:60%;
                input{
                  ${
                    creditCardType
                      ? `
                      background-image: url(${checkMark});
                      background-repeat: no-repeat;
                      background-position: 80% 50%;
                    `
                      : null
                  }
                }
              `}
            />
          </Inputmask>
          <Inputmask
            mask={expiryDateMask.mask}
            maskPlaceholder=""
            value={paymentInfo.cardExpirationDate}
            error={paymentError.cardExpirationDateError}
            onChange={(e) => {
              dispatch(setPaymentInfo({ cardExpirationDate: e.target.value }));
              if (paymentError.cardExpirationDateError) {
                setPaymentError((paymentError) => ({
                  ...paymentError,
                  cardExpirationDateError: false,
                }));
              }
            }}
          >
            <Input
              required
              title="EXPIRY"
              additionalstyle={`
                width: 20%;
              `}
            />
          </Inputmask>
          <Inputmask
            mask={cvvMask.mask}
            maskPlaceholder=""
            value={paymentInfo.cardCVV}
            error={paymentError.cardCVVError}
            onChange={(e) => {
              dispatch(setPaymentInfo({ cardCVV: e.target.value }));
              if (paymentError.cardCVVError) {
                setPaymentError((paymentError) => ({
                  ...paymentError,
                  cardCVVError: false,
                }));
              }
            }}
          >
            <Input
              required
              title="CCV"
              additionalstyle={`
                width: 20%;
              `}
            />
          </Inputmask>
        </Row>
        <Row>
          <div className="address">
            <Input
              required
              title="STREET ADDRESS"
              inputFieldRef={ref}
              value={paymentInfo.streetAddress}
              error={paymentError.streetAddressError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    streetAddress: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
          <div className="address">
            <Input
              title="STREET ADDRESS LINE 2"
              value={paymentInfo.streetAddressLine2}
              error={paymentError.streetAddressLine2Error}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    streetAddressLine2: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
        </Row>
        <Row>
          <div className="city">
            <Input
              title="CITY"
              value={paymentInfo.city}
              error={paymentError.cityError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    city: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
          <div className="province">
            <Input
              title="PROVINCE / STATE"
              value={paymentInfo.province}
              error={paymentError.provinceError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    province: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
          <div className="zipCode">
            <Input
              title="POSTAL / ZIP CODE"
              value={paymentInfo.postalCode}
              error={paymentError.postalCodeError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    postalCode: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
          <div className="country">
            <Input
              title="COUNTRY"
              value={paymentInfo.country}
              error={paymentError.countryError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    country: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
        </Row>
        <Row>
          <Checkbox
            checked={isOTAPrepayment}
            onChange={() => setIsOTAPrepayment(!isOTAPrepayment)}
            title="Use this card for the OTA prepayment"
          />
        </Row>
      </Content>
      <Footer>
        <Button
          success
          additionalstyle={`
            width: 104px;
          `}
          onClick={handleContinue}
          data-loading={loading}
        >
          {loading ? null : "Continue"}
        </Button>
      </Footer>
      <ModalGeneralSuccessfully
        isOpen={addCreditCardSuccessfullyIsOpen}
        setIsOpen={setAddCreditCardSuccessfullyIsOpen}
        redirectFunction={() => navigate(`/group-check-in/print-key/${groupId}`)}
        type="checkInPayment"
      />
      <ModalGeneralFailed
        isOpen={addCreditCardFailedIsOpen}
        setIsOpen={setAddCreditCardFailedIsOpen}
        type="checkInPayment"
        errorMessage={errorMessage}
        retryFunction={handleContinue}
      />
      <ModalPartialAuthorizationAccepted
        isOpen={partialAuthorizationIsOpen}
        setIsOpen={setPartialAuthorizationIsOpen}
        amount={credit}
        balance={balance}
        retryFunction={handleContinue}
      />
    </Container>
  );
};

export default CreditCard;
